import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import Layout from './components/Layout';
import SuperAdminDashboardRedirect from './components/SuperAdminDashboardRedirect';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import Dashboard from './pages/Dashboard';
import Projects from './pages/Projects';
import Timesheets from './pages/Timesheets';
import CreateTimesheet from './pages/CreateTimesheet';
import EditTimesheet from './pages/EditTimesheet';
import TimesheetDetails from './pages/TimesheetDetails';
import AdminTimesheetDetails from './pages/AdminTimesheetDetails';
import TimesheetReview from './pages/TimesheetReview';
import EmployeeManagement from './pages/EmployeeManagement';
import EmployeeList from './pages/EmployeeList';
import ProjectManagement from './pages/ProjectManagement';
import PasswordManagement from './pages/PasswordManagement';
import Settings from './pages/Settings';
import AdminDashboard from './pages/AdminDashboard';
import AdminTimesheets from './pages/AdminTimesheets';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import UserManagement from './pages/UserManagement';
import Profile from './pages/Profile';
import ManagerDashboard from './pages/ManagerDashboard';
import ManagerEmployeeManagement from './pages/ManagerEmployeeManagement';
import ManagerProjectManagement from './pages/ManagerProjectManagement';
import ManagerTimesheets from './pages/ManagerTimesheets';
import ManagerTimesheetDetails from './pages/ManagerTimesheetDetails';
import ConsolidatedTimesheetExport from './pages/ConsolidatedTimesheetExport';
import EmployeeTimesheetDetail from './pages/EmployeeTimesheetDetail';
import AdminDraftReport from './pages/AdminDraftReport';
import AdminDraftProjectDay from './pages/AdminDraftProjectDay';
import AOS from 'aos';
import 'aos/dist/aos.css';

// Component to handle unauthorized events
function UnauthorizedHandler() {
  const navigate = useNavigate();

  useEffect(() => {
    const handleUnauthorized = (event) => {
      navigate(event.detail.path, { replace: true });
    };

    window.addEventListener('unauthorized', handleUnauthorized);
    return () => {
      window.removeEventListener('unauthorized', handleUnauthorized);
    };
  }, [navigate]);

  return null;
}

function AppContent() {
  const { user, loading } = useAuth();

  useEffect(() => {
    AOS.init({
      duration: 1000,
      easing: 'ease-in-out',
      once: true,
      disable: false,
      startEvent: 'DOMContentLoaded',
      initClassName: 'aos-init',
      animatedClassName: 'aos-animate',
    });
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <Router>
      <UnauthorizedHandler />
      <Routes>
        <Route path="/login" element={!user ? <Login /> : <Navigate to="/" replace />} />
        <Route path="/forgot-password" element={!user ? <ForgotPassword /> : <Navigate to="/" replace />} />
        
        <Route path="/" element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }>
          <Route index element={<SuperAdminDashboardRedirect />} />
          <Route path="dashboard" element={
            <ProtectedRoute allowedRoles={['EMPLOYEE']}>
              <Dashboard />
            </ProtectedRoute>
          } />
          <Route path="projects" element={
            <ProtectedRoute allowedRoles={['EMPLOYEE']}>
              <Projects />
            </ProtectedRoute>
          } />
          <Route path="timesheets" element={<Timesheets />} />
          <Route path="timesheets/create" element={<CreateTimesheet />} />
          <Route path="timesheets/:id/edit" element={<EditTimesheet />} />
          <Route path="timesheets/:id" element={<TimesheetDetails />} />
          <Route path="profile" element={<Profile />} />
          
          {/* Admin Routes */}
          <Route path="admin" element={
            <ProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
              <AdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="admin/timesheets/review" element={
            <ProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
              <TimesheetReview />
            </ProtectedRoute>
          } />
          <Route path="admin/timesheets/:id" element={
            <ProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
              <AdminTimesheetDetails />
            </ProtectedRoute>
          } />
          <Route path="admin/users/:userId/timesheets" element={
            <ProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
              <EmployeeTimesheetDetail />
            </ProtectedRoute>
          } />
          <Route path="admin/timesheets" element={
            <ProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
              <AdminTimesheets />
            </ProtectedRoute>
          } />
          <Route path="admin/employees" element={
            <ProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
              <EmployeeManagement />
            </ProtectedRoute>
          } />
          <Route path="admin/employee-list" element={
            <ProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
              <EmployeeList />
            </ProtectedRoute>
          } />
          <Route path="admin/projects" element={
            <ProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
              <ProjectManagement />
            </ProtectedRoute>
          } />
          <Route path="admin/consolidated-export" element={
            <ProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
              <ConsolidatedTimesheetExport />
            </ProtectedRoute>
          } />
          <Route path="admin/passwords" element={
            <ProtectedRoute allowedRoles={['ADMIN']}>
              <PasswordManagement />
            </ProtectedRoute>
          } />
          <Route path="admin/draft-report" element={
            <ProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
              <AdminDraftReport />
            </ProtectedRoute>
          } />
          <Route path="admin/draft-report/project" element={
            <ProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
              <AdminDraftProjectDay />
            </ProtectedRoute>
          } />
          
          {/* Manager Routes */}
          <Route path="manager" element={
            <ProtectedRoute allowedRoles={['MANAGER']}>
              <ManagerDashboard />
            </ProtectedRoute>
          } />
          <Route path="manager/employees" element={
            <ProtectedRoute allowedRoles={['MANAGER']}>
              <ManagerEmployeeManagement />
            </ProtectedRoute>
          } />
          <Route path="manager/projects" element={
            <ProtectedRoute allowedRoles={['MANAGER']}>
              <ManagerProjectManagement />
            </ProtectedRoute>
          } />
          <Route path="manager/timesheets" element={
            <ProtectedRoute allowedRoles={['MANAGER']}>
              <ManagerTimesheets />
            </ProtectedRoute>
          } />
          <Route path="manager/timesheets/:id" element={
            <ProtectedRoute allowedRoles={['MANAGER']}>
              <ManagerTimesheetDetails />
            </ProtectedRoute>
          } />
          <Route path="manager/users/:userId/timesheets" element={
            <ProtectedRoute allowedRoles={['MANAGER']}>
              <EmployeeTimesheetDetail />
            </ProtectedRoute>
          } />
          
          {/* Super Admin Routes */}
          <Route path="super-admin" element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <SuperAdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="super-admin/users" element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <UserManagement />
            </ProtectedRoute>
          } />
          <Route path="super-admin/passwords" element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <PasswordManagement />
            </ProtectedRoute>
          } />
          <Route path="super-admin/timesheets" element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <TimesheetReview />
            </ProtectedRoute>
          } />
          <Route path="super-admin/employees" element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <EmployeeManagement />
            </ProtectedRoute>
          } />
          <Route path="super-admin/employee-list" element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <EmployeeList />
            </ProtectedRoute>
          } />
          <Route path="super-admin/consolidated-export" element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <ConsolidatedTimesheetExport />
            </ProtectedRoute>
          } />
          <Route path="super-admin/settings" element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <Settings />
            </ProtectedRoute>
          } />
          <Route path="super-admin/timesheets/:id" element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <AdminTimesheetDetails />
            </ProtectedRoute>
          } />
          <Route path="super-admin/users/:userId/timesheets" element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <EmployeeTimesheetDetail />
            </ProtectedRoute>
          } />
        </Route>
      </Routes>
    </Router>
  );
}

function App() {
  return (
    <AuthProvider>
      <NotificationProvider>
        <AppContent />
      </NotificationProvider>
    </AuthProvider>
  );
}

export default App;

